import React, { useState, useRef, useEffect } from 'react';
import { Edit, Trash2, Info } from 'lucide-react';
import {
  IconChecklist,
  IconNote,
  IconAlertTriangle,
} from '@tabler/icons-react';
import styles from './MapContextMenu.module.css';

interface MenuPosition {
  x: number;
  y: number;
}

export interface ContextMenuItem {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  variant?: 'default' | 'danger';
}

interface MapContextMenuProps {
  isVisible: boolean;
  position: MenuPosition;
  items: ContextMenuItem[];
  onClose: () => void;
}

export const MapContextMenu = ({
  isVisible,
  position,
  items,
  onClose,
}: MapContextMenuProps) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const [adjustedPosition, setAdjustedPosition] =
    useState<MenuPosition>(position);

  // Adjust position to keep menu within viewport
  useEffect(() => {
    if (!isVisible || !menuRef.current) return;

    const menu = menuRef.current;
    const rect = menu.getBoundingClientRect();
    const viewport = {
      width: window.innerWidth,
      height: window.innerHeight,
    };

    let adjustedX = position.x;
    let adjustedY = position.y;

    // Adjust horizontal position
    if (position.x + rect.width > viewport.width) {
      adjustedX = viewport.width - rect.width - 10;
    }

    // Adjust vertical position
    if (position.y + rect.height > viewport.height) {
      adjustedY = viewport.height - rect.height - 10;
    }

    setAdjustedPosition({ x: adjustedX, y: adjustedY });
  }, [isVisible, position]);

  // Handle click/touch outside to close
  // Desktop: mousedown, Escape, wheel
  // Mobile: touchstart (critical for "close on any interaction" behavior)
  useEffect(() => {
    if (!isVisible) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        // NOTE: Do NOT suppress interactions here - context menu closed by click
        // should allow new interactions (e.g., right-click to open new menu)
        onClose();
      }
    };

    // Mobile: touchstart handler for "close on any interaction"
    // This ensures only one context menu is visible at a time on mobile
    const handleTouchStart = (event: TouchEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        // NOTE: Do NOT suppress interactions here - context menu closed by tap
        // should allow new interactions at that location
        onClose();
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    const handleWheel = () => {
      onClose();
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleTouchStart, {
      passive: true,
    });
    document.addEventListener('keydown', handleEscape);
    document.addEventListener('wheel', handleWheel);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('wheel', handleWheel);
    };
  }, [isVisible, onClose]);

  if (!isVisible) return null;

  return (
    <div
      ref={menuRef}
      className={`${styles.mapContextMenu} semi-transparent-panel`}
      style={
        {
          '--menu-x': `${adjustedPosition.x}px`,
          '--menu-y': `${adjustedPosition.y}px`,
        } as React.CSSProperties
      }
    >
      {items.map((item, index) => (
        <React.Fragment key={index}>
          <button
            className={`${styles.contextMenuItem} ${
              item.variant === 'danger' ? styles.danger : ''
            }`}
            onClick={() => {
              item.onClick();
              onClose();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                item.onClick();
                onClose();
              }
            }}
          >
            <span className={styles.contextMenuIcon}>{item.icon}</span>
            <span className={styles.contextMenuLabel}>{item.label}</span>
          </button>
          {/* Add separator after "Show Details" and before "Create Task Here" */}
          {(item.label.includes('details') ||
            item.label.includes('szczegóły') ||
            item.label.includes('Delete') ||
            item.label.includes('Usuń')) &&
            index < items.length - 1 && (
              <div className={styles.menuSeparator} />
            )}
        </React.Fragment>
      ))}
    </div>
  );
};

// Helper functions to create common menu items
// eslint-disable-next-line react-refresh/only-export-components
export const createTaskMenuItems = (
  onShowDetails: () => void,
  onEdit: () => void,
  onDelete: () => void,
  onCreateHere: () => void,
  t: (key: string) => string
): ContextMenuItem[] => [
  {
    icon: <Info size={14} />,
    label: t('contextMenu.showDetails'),
    onClick: onShowDetails,
  },
  {
    icon: <Edit size={14} />,
    label: t('common.editTask'),
    onClick: onEdit,
  },
  {
    icon: <Trash2 size={14} />,
    label: t('common.deleteTask'),
    onClick: onDelete,
    variant: 'danger',
  },
  {
    icon: <IconChecklist size={14} />,
    label: t('contextMenu.createTaskHere'),
    onClick: onCreateHere,
  },
];

export interface CreateMapMenuItemsOptions {
  t: (key: string) => string;
  onCreateTask: () => void;
  onCreateNoteHere?: () => void;
  onCreateObstructionHere?: () => void;
}

// eslint-disable-next-line react-refresh/only-export-components
export const createMapMenuItems = ({
  t,
  onCreateTask,
  onCreateNoteHere,
  onCreateObstructionHere,
}: CreateMapMenuItemsOptions): ContextMenuItem[] => {
  const items: ContextMenuItem[] = [
    {
      icon: <IconChecklist size={14} />,
      label: t('contextMenu.createTaskHere'),
      onClick: onCreateTask,
    },
  ];

  if (onCreateNoteHere) {
    items.push({
      icon: <IconNote size={14} />,
      label: t('contextMenu.createNoteHere'),
      onClick: onCreateNoteHere,
    });
  }

  if (onCreateObstructionHere) {
    items.push({
      icon: <IconAlertTriangle size={14} />,
      label: t('contextMenu.createObstructionHere'),
      onClick: onCreateObstructionHere,
    });
  }

  return items;
};
